import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Select from 'react-select';
import PropTypes from 'prop-types';
import { Form } from 'react-bootstrap';
import { map, find, isArray, isEmpty, isEqual } from 'lodash';
import { fetchSections } from 'actions';
import { F } from 'pages/AppraisalManagment/constants/legacyApiKeys';
import { CustomDropdownContainer, CustomMultiOption } from 'CommonComponent';

/**
 * Multi-select dropdown for appraisal sections (list endpoint under legacy URL path).
 */
const AppraisalSectionDropdown = ({
  selectedId = '',
  selectedChangingId = '',
  sectionValue,
  setSectionValue,
  sectionOnChange,
  setAllSectionIds,
  showSelectAllOption = true,
  isMulti = true,
  placeholder = 'Choose / select Sections',
  label = 'Sections',
  name = 'section_id',
  isDisabled = false,
  required = false,
}) => {
  const dispatch = useDispatch();
  const sectionList = useSelector((state) => state.sectionState.sectionsList);
  const session = useSelector((state) => state.session);
  const [options, setOptions] = useState([]);

  const object_viewed_id =
    (session?.current_page_permissions && !Array.isArray(session.current_page_permissions)
      ? session.current_page_permissions.object_id
      : '') ||
    session?.current_object_viewed_id ||
    '';

  useEffect(() => {
    if (!object_viewed_id) return;
    dispatch(fetchSections(object_viewed_id));
    // Intentionally omit object_viewed_id from ongoing updates: Main.js swaps
    // permissions mid-navigation and would re-fetch while the form unmounts.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dispatch]);

  useEffect(() => {
    if (!sectionList || !Array.isArray(sectionList)) return;

    let formattedOptions = map(sectionList, (item) => ({
      value: item.id,
      label: item.title || item[F.legacyNameSynonym],
    }));

    if (showSelectAllOption && isMulti) {
      formattedOptions.unshift({ value: 'all', label: 'All' });
    }

    setOptions(formattedOptions);

    if (typeof setAllSectionIds === 'function') {
      setAllSectionIds(map(sectionList, 'id'));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setSectionValue may be recreated each render
  }, [sectionList, showSelectAllOption, isMulti]);

  // Hydrate from selectedId only when IDs/list change — not on every value edit.
  useEffect(() => {
    if (isEmpty(selectedId) || !sectionList || !Array.isArray(sectionList)) return;

    const formattedOptions = map(sectionList, (item) => ({
      value: item.id,
      label: item.title || item[F.legacyNameSynonym],
    }));

    if (isArray(selectedId)) {
      const uniqueIds = [...new Set(selectedId.map((id) => String(id)))];
      const selected = uniqueIds
        .map((id) => find(formattedOptions, (opt) => opt && String(opt.value) === String(id)))
        .filter(Boolean);
      if (!isEqual(sectionValue, selected)) {
        setSectionValue(selected);
      }
    } else {
      const selected = find(
        formattedOptions,
        (opt) => opt && String(opt.value) === String(selectedId)
      );
      if (!isEqual(sectionValue, selected || null)) {
        setSectionValue(selected || null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionList, selectedId]);

  useEffect(() => {
    if (!selectedChangingId || !sectionList) return;

    if (isArray(selectedChangingId)) {
      const selected = selectedChangingId
        .map((id) => find(sectionList, (item) => item && String(item.id) === String(id)))
        .filter(Boolean)
        .map((item) => ({
          value: item.id,
          label: item.title || item[F.legacyNameSynonym],
        }));
      setSectionValue(selected);
    } else {
      const selected = find(
        sectionList,
        (item) => item && String(item.id) === String(selectedChangingId)
      );
      setSectionValue(
        selected
          ? { value: selected.id, label: selected.title || selected[F.legacyNameSynonym] }
          : null
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedChangingId, sectionList]);

  return (
    <Form.Group className="mb-3">
      <Form.Label className={`label-input${required ? " label-input--required" : ""}`}>{label}</Form.Label>
      <Select
        name={name}
        options={options}
        value={sectionValue}
        onChange={sectionOnChange}
        isMulti={isMulti}
        placeholder={placeholder}
        className="multi-select w-100"
        classNamePrefix="react-select"
        isSearchable
        isClearable
        isDisabled={isDisabled}
        hideSelectedOptions={false}
        closeMenuOnSelect={!isMulti}
        openMenuOnFocus
        components={{
          ValueContainer: CustomDropdownContainer,
          ...(isMulti ? { Option: CustomMultiOption } : {}),
        }}
      />
    </Form.Group>
  );
};

AppraisalSectionDropdown.propTypes = {
  sectionOnChange: PropTypes.func.isRequired,
  setSectionValue: PropTypes.func.isRequired,
  sectionValue: PropTypes.any,
  selectedId: PropTypes.any,
  selectedChangingId: PropTypes.any,
  setAllSectionIds: PropTypes.func,
  isMulti: PropTypes.bool,
  label: PropTypes.string,
  placeholder: PropTypes.string,
  name: PropTypes.string,
};

export default AppraisalSectionDropdown;
